var _excluded = ["className", "children", "containerProps"],
  _excluded2 = ["className"],
  _excluded3 = ["className"],
  _excluded4 = ["className"],
  _excluded5 = ["className"],
  _excluded6 = ["onClick", "className"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import * as React from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import { forwardRef, StylesProvider, tqc, useMultiStyleConfig, useStyles } from '../../system';
import { callAllHandlers, createContext, cx, __DEV__ } from '../../utils';
import { CloseButton } from '../close-button';
import { FocusLock } from '../focus-lock';
import { Portal } from '../portal';
import { Fade, ScaleFade } from '../transition';
import { useModal } from './use-modal';
var [ModalContextProvider, useModalContext] = createContext({
  strict: true,
  name: 'ModalContext',
  errorMessage: 'useModalContext: `context` is undefined. Seems you forgot to wrap modal components in `<Modal />`'
});
export { ModalContextProvider, useModalContext };

/**
 * Modal provides context, theming, and accessibility properties
 * to all other modal components.
 *
 * It doesn't render any DOM node.
 */
export var Modal = props => {
  var {
    portalProps,
    children,
    autoFocus,
    trapFocus,
    initialFocusRef,
    finalFocusRef,
    returnFocusOnClose,
    blockScrollOnMount,
    allowPinchZoom,
    preserveScrollBarGap,
    motionPreset,
    lockFocusAcrossFrames
  } = props;
  var styles = useMultiStyleConfig('Modal', props);
  var modal = useModal(props);
  var context = _objectSpread(_objectSpread({}, modal), {}, {
    autoFocus,
    trapFocus,
    initialFocusRef,
    finalFocusRef,
    returnFocusOnClose,
    blockScrollOnMount,
    allowPinchZoom,
    preserveScrollBarGap,
    motionPreset,
    lockFocusAcrossFrames
  });
  return /*#__PURE__*/React.createElement(ModalContextProvider, {
    value: context
  }, /*#__PURE__*/React.createElement(StylesProvider, {
    value: styles
  }, /*#__PURE__*/React.createElement(Portal, portalProps, children)));
};
Modal.defaultProps = {
  lockFocusAcrossFrames: true,
  returnFocusOnClose: true,
  scrollBehavior: 'inside',
  trapFocus: true,
  autoFocus: true,
  blockScrollOnMount: true,
  allowPinchZoom: false,
  motionPreset: 'scale'
};
if (__DEV__) {
  Modal.displayName = 'Modal';
}
/**
 * ModalContent is used to group modal's content. It has all the
 * necessary `aria-*` properties to indicate that it is a modal
 */
export var ModalContent = forwardRef((props, ref) => {
  var {
      className,
      children,
      containerProps: rootProps
    } = props,
    rest = _objectWithoutProperties(props, _excluded);
  var {
    getDialogProps,
    getDialogContainerProps,
    isOpen,
    motionPreset
  } = useModalContext();
  var dialogProps = getDialogProps(rest, ref);
  var containerProps = getDialogContainerProps(rootProps);
  var _className = cx('tqc-modal__content', className);
  var styles = useStyles();
  var dialogStyles = _objectSpread({
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    width: '100%',
    outline: 0
  }, styles.dialog);
  var dialogContainerStyles = _objectSpread({
    display: 'flex',
    width: '100vw',
    height: 'calc(100vh - calc(100vh - 100%))',
    position: 'fixed',
    left: 0,
    top: 0
  }, styles.dialogContainer);
  if (motionPreset === 'none') {
    return isOpen ? /*#__PURE__*/React.createElement(ModalFocusScope, null, /*#__PURE__*/React.createElement(tqc.div, _extends({
      __css: dialogContainerStyles
    }, containerProps, {
      className: "tqc-modal__content-container"
    }), /*#__PURE__*/React.createElement(tqc.div, _extends({
      __css: _objectSpread({}, dialogStyles)
    }, dialogProps, {
      className: _className
    }), children))) : /*#__PURE__*/React.createElement(React.Fragment, null);
  }
  return /*#__PURE__*/React.createElement(ScaleFade, {
    in: isOpen,
    unmountOnExit: true,
    duration: "fast"
  }, _ref => {
    var {
      transitionStyles
    } = _ref;
    return /*#__PURE__*/React.createElement(ModalFocusScope, null, /*#__PURE__*/React.createElement(tqc.div, _extends({
      __css: dialogContainerStyles
    }, containerProps, {
      className: "tqc-modal__content-container"
    }), /*#__PURE__*/React.createElement(tqc.div, _extends({
      __css: _objectSpread(_objectSpread({}, dialogStyles), transitionStyles)
    }, dialogProps, {
      className: _className
    }), children)));
  });
});
if (__DEV__) {
  ModalContent.displayName = 'ModalContent';
}
export function ModalFocusScope(props) {
  var {
    autoFocus,
    trapFocus,
    dialogRef,
    initialFocusRef,
    blockScrollOnMount,
    allowPinchZoom,
    finalFocusRef,
    returnFocusOnClose,
    preserveScrollBarGap,
    lockFocusAcrossFrames
  } = useModalContext();
  return /*#__PURE__*/React.createElement(FocusLock, {
    autoFocus: autoFocus,
    isDisabled: !trapFocus,
    initialFocusRef: initialFocusRef,
    finalFocusRef: finalFocusRef,
    restoreFocus: returnFocusOnClose,
    contentRef: dialogRef,
    lockFocusAcrossFrames: lockFocusAcrossFrames
  }, /*#__PURE__*/React.createElement(RemoveScroll, {
    removeScrollBar: !preserveScrollBarGap,
    allowPinchZoom: allowPinchZoom,
    enabled: blockScrollOnMount,
    forwardProps: true
  }, props.children));
}
/**
 * ModalOverlay renders a backdrop behind the modal. It is
 * also used as a wrapper for the modal content for better positioning.
 */
export var ModalOverlay = forwardRef((props, ref) => {
  var {
      className
    } = props,
    rest = _objectWithoutProperties(props, _excluded2);
  var _className = cx('tqc-modal__overlay', className);
  var styles = useStyles();
  var overlayStyle = _objectSpread({
    pos: 'fixed',
    left: '0',
    top: '0',
    w: '100vw',
    h: '100vh'
  }, styles.overlay);
  var context = useModalContext();
  var overlayElement = /*#__PURE__*/React.createElement(tqc.div, _extends({
    __css: overlayStyle,
    ref: ref,
    className: _className
  }, rest));
  if (context.motionPreset === 'none') {
    return context.isOpen ? overlayElement : /*#__PURE__*/React.createElement(React.Fragment, null);
  }
  return /*#__PURE__*/React.createElement(Fade, {
    in: context.isOpen
  }, overlayElement);
});
if (__DEV__) {
  ModalOverlay.displayName = 'ModalOverlay';
}
/**
 * ModalHeader
 *
 * React component that houses the title of the modal.
 */
export var ModalHeader = forwardRef((props, ref) => {
  var {
      className
    } = props,
    rest = _objectWithoutProperties(props, _excluded3);
  var {
    headerId,
    setHeaderMounted
  } = useModalContext();

  /**
   * Notify us if this component was rendered or used
   * so we can append `aria-labelledby` automatically
   */
  React.useEffect(() => {
    setHeaderMounted(true);
    return () => setHeaderMounted(false);
  }, [setHeaderMounted]);
  var _className = cx('tqc-modal__header', className);
  var styles = useStyles();
  var headerStyles = _objectSpread({
    flex: 0
  }, styles.header);
  return /*#__PURE__*/React.createElement(tqc.header, _extends({
    ref: ref,
    className: _className,
    id: headerId
  }, rest, {
    __css: headerStyles
  }));
});
if (__DEV__) {
  ModalHeader.displayName = 'ModalHeader';
}
/**
 * ModalBody
 *
 * React component that houses the main content of the modal.
 */
export var ModalBody = forwardRef((props, ref) => {
  var {
      className
    } = props,
    rest = _objectWithoutProperties(props, _excluded4);
  var {
    bodyId,
    setBodyMounted
  } = useModalContext();

  /**
   * Notify us if this component was rendered or used
   * so we can append `aria-describedby` automatically
   */
  React.useEffect(() => {
    setBodyMounted(true);
    return () => setBodyMounted(false);
  }, [setBodyMounted]);
  var _className = cx('tqc-modal__body', className);
  var styles = useStyles();
  return /*#__PURE__*/React.createElement(tqc.div, _extends({
    ref: ref,
    className: _className,
    id: bodyId
  }, rest, {
    __css: styles.body
  }));
});
if (__DEV__) {
  ModalBody.displayName = 'ModalBody';
}
/**
 * ModalFooter houses the action buttons of the modal.
 */
export var ModalFooter = forwardRef((props, ref) => {
  var {
      className
    } = props,
    rest = _objectWithoutProperties(props, _excluded5);
  var _className = cx('tqc-modal__footer', className);
  var styles = useStyles();
  var footerStyles = _objectSpread({
    display: 'flex',
    alignItems: 'center'
  }, styles.footer);
  return /*#__PURE__*/React.createElement(tqc.footer, _extends({
    ref: ref
  }, rest, {
    __css: footerStyles,
    className: _className
  }));
});
if (__DEV__) {
  ModalFooter.displayName = 'ModalFooter';
}

/**
 * ModalCloseButton is used closes the modal.
 *
 * You don't need to pass the `onClick` to it, it reads the
 * `onClose` action from the modal context.
 */
export var ModalCloseButton = forwardRef((props, ref) => {
  var {
      onClick,
      className
    } = props,
    rest = _objectWithoutProperties(props, _excluded6);
  var {
    onClose
  } = useModalContext();
  var _className = cx('tqc-modal__close-btn', className);
  return /*#__PURE__*/React.createElement(CloseButton, _extends({
    ref: ref,
    ml: "auto",
    className: _className,
    onClick: callAllHandlers(onClick, event => {
      event.stopPropagation();
      onClose();
    })
  }, rest));
});
if (__DEV__) {
  ModalCloseButton.displayName = 'ModalCloseButton';
}